import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';

// WhatsApp's API never exposes customers' real profile pictures, so we render
// deterministic colored initials instead (same approach as WhatsApp Web /
// Telegram placeholders). Medium-dark tones chosen for white-text contrast.
const AVATAR_COLORS = [
  '#dc2626', // red
  '#ea580c', // orange
  '#b45309', // amber
  '#16a34a', // green
  '#0d9488', // teal
  '#0891b2', // cyan
  '#0284c7', // sky
  '#4f46e5', // indigo
  '#7c3aed', // violet
  '#9333ea', // purple
  '#db2777', // pink
  '#e11d48', // rose
];

export function getAvatarInitials(contactName?: string, phoneNumber?: string): string {
  if (contactName) {
    const words = contactName.trim().split(/\s+/);
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return contactName.slice(0, 2).toUpperCase();
  }

  if (phoneNumber) {
    const digits = phoneNumber.replace(/\D/g, '');
    return digits.slice(-2);
  }

  return '??';
}

// Stable color per contact: hash the phone number (falling back to the name)
// so the same chat always gets the same color across sessions and views.
export function getAvatarColor(contactName?: string, phoneNumber?: string): string {
  const seed = phoneNumber || contactName || '';
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

type Props = {
  contactName?: string;
  phoneNumber?: string;
  className?: string;
  textClassName?: string;
};

export function ContactAvatar({ contactName, phoneNumber, className, textClassName }: Props) {
  return (
    <Avatar className={className}>
      <AvatarFallback
        className={cn('text-white font-medium', textClassName)}
        style={{ backgroundColor: getAvatarColor(contactName, phoneNumber) }}
      >
        {getAvatarInitials(contactName, phoneNumber)}
      </AvatarFallback>
    </Avatar>
  );
}
